import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Zap, Trophy, Users, Bell,
  ChevronUp, Flame, Star, Clock, TrendingUp,
  Target, Crown, ChevronRight, AlertCircle, Gift,
} from 'lucide-react';

// ─── Live Shift Dashboard ───────────────────────────────────────────
// The main screen during a shift. Everything a server needs at a glance:
// their earnings, active bounties, live leaderboard, AI tips.

interface BountyNotification {
  id: number;
  type: 'flash' | 'team' | 'personal';
  title: string;
  description: string;
  reward: string;
  timeLeft: string;
  urgency: 'hot' | 'normal';
  progress?: { current: number; target: number };
}

const ACTIVE_BOUNTIES: BountyNotification[] = [
  {
    id: 1, type: 'flash', title: 'Halibut Hustle',
    description: 'Kitchen has 8 halibut left. First 2 servers to sell one get the bonus.',
    reward: '$10 instant', timeLeft: '42 min left', urgency: 'hot',
    progress: { current: 0, target: 1 },
  },
  {
    id: 2, type: 'team', title: 'Happy Hour Hero',
    description: 'Team sells 20 cocktails before 7 PM. Everyone on shift gets a bonus.',
    reward: '$5 each', timeLeft: '1h 15m left', urgency: 'normal',
    progress: { current: 14, target: 20 },
  },
  {
    id: 3, type: 'personal', title: 'Triple Play',
    description: 'Upsell 3 different menu categories in one shift (appetizer, entree, dessert).',
    reward: '$8 bonus', timeLeft: 'All shift', urgency: 'normal',
    progress: { current: 2, target: 3 },
  },
];

interface LeaderboardEntry {
  name: string;
  avatar: string;
  upsells: number;
  earnings: number;
  isYou: boolean;
}

const LIVE_BOARD: LeaderboardEntry[] = [
  { name: 'Sarah C.', avatar: 'SC', upsells: 7, earnings: 48.20, isYou: false },
  { name: 'You', avatar: 'YO', upsells: 5, earnings: 36.40, isYou: true },
  { name: 'James T.', avatar: 'JT', upsells: 4, earnings: 28.80, isYou: false },
  { name: 'Maria G.', avatar: 'MG', upsells: 3, earnings: 21.60, isYou: false },
  { name: 'Kevin L.', avatar: 'KL', upsells: 2, earnings: 14.40, isYou: false },
];

interface CheatCode {
  context: string;
  script: string;
  why: string;
}

const CURRENT_CHEAT: CheatCode = {
  context: 'Table of 4 just sat down, it\'s 6:30 PM, and they look like they\'re celebrating',
  script: '"Welcome! Are we celebrating something tonight? Perfect — can I start you off with a round of our house margaritas? They\'re incredible."',
  why: 'Celebration tables order 40% more when you acknowledge it. Lead with drinks while they\'re in a good mood.',
};

const ShiftLive: React.FC = () => {
  const navigate = useNavigate();
  const [showNotification, setShowNotification] = useState(false);
  const [dismissedNotif, setDismissedNotif] = useState(false);

  // Simulate a flash bounty notification
  useEffect(() => {
    const timer = setTimeout(() => setShowNotification(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const yourEarnings = 142.60;
  const upsellExtra = 36.40;
  const shiftHours = 2.5;
  const pacePerHour = yourEarnings / shiftHours;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Push notification overlay */}
      {showNotification && !dismissedNotif && (
        <div className="fixed top-0 left-0 right-0 z-50 p-4 animate-slide-down">
          <div className="max-w-sm mx-auto bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-4 shadow-2xl shadow-orange-500/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] font-black text-orange-200 uppercase">Flash Bounty</span>
                  <span className="text-[10px] text-orange-200">just now</span>
                </div>
                <h3 className="text-sm font-bold text-white mb-0.5">Ribeye Rush!</h3>
                <p className="text-xs text-orange-100">Chef wants to move ribeyes tonight. Next server to sell one gets <span className="font-bold">$15 instant bonus</span>. Script ready in app.</p>
              </div>
              <button onClick={() => setDismissedNotif(true)} className="text-orange-200 hover:text-white">
                <span className="text-lg">&times;</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gray-950 border-b border-gray-900 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => navigate('/restaurant/mockups')} className="text-gray-500 hover:text-gray-300">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-gray-400">ON SHIFT</span>
            </div>
            <Bell className="w-5 h-5 text-gray-500" />
          </div>

          {/* Earnings bar */}
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs text-gray-500">Tonight so far</div>
              <div className="text-3xl font-bold text-white">${yourEarnings.toFixed(2)}</div>
            </div>
            <div className="text-right">
              <span className="text-sm text-emerald-400 font-semibold">+${upsellExtra.toFixed(2)}</span>
              <div className="text-[10px] text-gray-500">from upsells</div>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
            <span>${pacePerHour.toFixed(0)}/hr pace</span>
            <span>2.5h in</span>
            <span>5 upsells landed</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Active Bounties */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-amber-400" /> Active Bounties
            </h2>
            <span className="text-xs text-gray-500">{ACTIVE_BOUNTIES.length} live</span>
          </div>
          <div className="space-y-2">
            {ACTIVE_BOUNTIES.map((bounty) => (
              <div
                key={bounty.id}
                className={`rounded-xl border p-4 ${
                  bounty.urgency === 'hot'
                    ? 'bg-orange-500/5 border-orange-500/30'
                    : 'bg-gray-900 border-gray-800'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${
                      bounty.type === 'flash' ? 'bg-orange-500/20 text-orange-400' :
                      bounty.type === 'team' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-purple-500/20 text-purple-400'
                    }`}>
                      {bounty.type === 'flash' ? 'FLASH' : bounty.type === 'team' ? 'TEAM' : 'PERSONAL'}
                    </span>
                    {bounty.urgency === 'hot' && (
                      <span className="text-[10px] text-orange-400 font-medium animate-pulse">HOT</span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-emerald-400">{bounty.reward}</span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-0.5">{bounty.title}</h3>
                <p className="text-xs text-gray-500 mb-2">{bounty.description}</p>

                {bounty.progress && (
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          bounty.urgency === 'hot' ? 'bg-orange-400' : 'bg-emerald-400'
                        }`}
                        style={{ width: `${(bounty.progress.current / bounty.progress.target) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{bounty.progress.current}/{bounty.progress.target}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <Clock className="w-3 h-3" /> {bounty.timeLeft}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Cheat Code */}
        <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Zap className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-indigo-300 uppercase tracking-wider">AI Cheat Code</h3>
              <p className="text-[10px] text-gray-500">Context-aware • Updates per table</p>
            </div>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3 mb-2">
            <p className="text-[11px] text-gray-500 mb-1">{CURRENT_CHEAT.context}</p>
            <p className="text-sm text-indigo-200 italic leading-relaxed">{CURRENT_CHEAT.script}</p>
          </div>
          <p className="text-[11px] text-gray-600">{CURRENT_CHEAT.why}</p>
        </div>

        {/* Live Leaderboard */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
              <Trophy className="w-4 h-4 text-amber-400" /> Live Shift Board
            </h2>
            <span className="text-xs text-gray-500">Updates every sale</span>
          </div>
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            {LIVE_BOARD.map((entry, idx) => (
              <div
                key={entry.name}
                className={`flex items-center gap-3 px-4 py-2.5 ${
                  entry.isYou ? 'bg-amber-500/5 border-l-2 border-l-amber-400' :
                  idx < LIVE_BOARD.length - 1 ? 'border-b border-gray-800/50' : ''
                }`}
              >
                <span className="w-5 text-center">
                  {idx === 0 ? <Crown className="w-4 h-4 text-amber-400 mx-auto" /> :
                   <span className="text-xs text-gray-600">{idx + 1}</span>}
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  entry.isYou ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-gray-800 text-gray-400'
                }`}>
                  {entry.avatar}
                </div>
                <div className="flex-1">
                  <span className={`text-sm ${entry.isYou ? 'text-amber-300 font-medium' : 'text-gray-300'}`}>
                    {entry.name}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">{entry.upsells} upsells</div>
                  <div className="text-sm font-bold text-emerald-400">+${entry.earnings.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-600 text-center mt-1.5">
            Sarah is $11.80 ahead of you. One Wagyu upsell closes the gap.
          </p>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => navigate('/restaurant/mockups/flash-bounties')}
            className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-left hover:border-gray-700 transition-colors"
          >
            <Zap className="w-4 h-4 text-amber-400 mb-1" />
            <div className="text-xs font-medium text-white">All Bounties</div>
            <div className="text-[10px] text-gray-500">3 active</div>
          </button>
          <button
            onClick={() => navigate('/restaurant/mockups/cheat-codes')}
            className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-left hover:border-gray-700 transition-colors"
          >
            <Target className="w-4 h-4 text-indigo-400 mb-1" />
            <div className="text-xs font-medium text-white">Cheat Codes</div>
            <div className="text-[10px] text-gray-500">5 scripts ready</div>
          </button>
        </div>
      </div>

      {/* Slide-down animation */}
      <style>{`
        @keyframes slide-down { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-down { animation: slide-down 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default ShiftLive;
